import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { C } from "../../theme/tokens";
import { FOREX_PAIRS, OTC_PAIRS, formatPairLabel } from "../../data/pairs";

/* Accordion-style pair picker matching the reference design: a
   clipped-corner row showing the current pair that expands in place
   into a scrollable list, instead of opening a native <select>
   popup. Kept separate from the shared PairSelect primitive (still
   a native <select> on Live Charts) since this pattern is specific
   to the Signals Bot flow. */
export function PairPickerPanel({ value, onChange, forexLabel, otcLabel }) {
  const [open, setOpen] = useState(false);

  const handleSelect = (pair) => {
    onChange(pair);
    setOpen(false);
  };

  return (
    <div
      style={{
        position: "relative",
        clipPath: "polygon(0 0, calc(100% - 22px) 0, 100% 22px, 100% 100%, 0 100%)",
        background: `linear-gradient(160deg, ${C.panel2}, ${C.panel1} 60%)`,
        border: `1px solid ${C.hairline}`,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 22,
          height: 22,
          background: `linear-gradient(135deg, transparent 49%, ${C.greenBright}22 50%)`,
          pointerEvents: "none",
        }}
      />

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          position: "relative",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 18px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 18,
              fontWeight: 600,
              color: C.primary,
              whiteSpace: "nowrap",
            }}
          >
            {formatPairLabel(value)}
          </span>
        </div>
        <ChevronDown
          size={16}
          color={C.secondary}
          style={{
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s ease",
          }}
        />
      </button>

      <div
        style={{
          maxHeight: open ? 260 : 0,
          overflowY: "auto",
          overflowX: "hidden",
          transition: "max-height .25s ease",
          borderTop: open ? `1px solid ${C.hairline}` : "none",
        }}
      >
        <PairGroup label={forexLabel} pairs={FOREX_PAIRS} value={value} onSelect={handleSelect} />
        <PairGroup label={otcLabel} pairs={OTC_PAIRS} value={value} onSelect={handleSelect} />
      </div>
    </div>
  );
}

function PairGroup({ label, pairs, value, onSelect }) {
  return (
    <div>
      <div
        style={{
          padding: "10px 18px 4px",
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: C.mutedB,
        }}
      >
        {label}
      </div>
      {pairs.map((p) => {
        const active = p === value;
        return (
          <button
            key={p}
            type="button"
            onClick={() => onSelect(p)}
            aria-current={active}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "11px 18px",
              background: active ? `${C.greenBright}14` : "transparent",
              border: "none",
              borderTop: `1px solid ${C.hairline}`,
              color: active ? C.primary : C.secondary,
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 13.5,
              fontWeight: active ? 600 : 500,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            {formatPairLabel(p)}
          </button>
        );
      })}
    </div>
  );
}
